import { useEffect, useCallback, useState } from 'react';
import { X, Download, ZoomIn, ZoomOut } from 'lucide-react';

interface ScreenshotModalProps {
  src: string;
  alt?: string;
  onClose: () => void;
  onDownload?: () => void;
}

export const ScreenshotModal = ({ src, alt = 'Screenshot', onClose, onDownload }: ScreenshotModalProps) => {
  const [zoom, setZoom] = useState(1);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(z + 0.25, 5));
    if (e.key === '-') setZoom((z) => Math.max(z - 0.25, 0.25));
    if (e.key === '0') setZoom(1);
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      {/* Toolbar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/60 to-transparent z-10">
        <span className="text-sm text-gray-300">{alt}</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setZoom((z) => Math.max(z - 0.25, 0.25))}
            className="p-1.5 rounded hover:bg-white/10 text-gray-300 hover:text-white"
            title="Zoom out (−)"
          >
            <ZoomOut size={18} />
          </button>
          <button
            onClick={() => setZoom(1)}
            className="px-2 py-1 rounded hover:bg-white/10 text-gray-300 hover:text-white text-xs font-mono min-w-[3.5rem] text-center"
            title="Reset zoom (0)"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            onClick={() => setZoom((z) => Math.min(z + 0.25, 5))}
            className="p-1.5 rounded hover:bg-white/10 text-gray-300 hover:text-white"
            title="Zoom in (+)"
          >
            <ZoomIn size={18} />
          </button>
          {onDownload && (
            <button
              onClick={onDownload}
              className="p-1.5 rounded hover:bg-white/10 text-gray-300 hover:text-white ml-2"
              title="Download"
            >
              <Download size={18} />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-white/10 text-gray-300 hover:text-white ml-2"
            title="Close (Esc)"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Image */}
      <div className="overflow-auto max-h-full max-w-full p-12">
        <img
          src={src}
          alt={alt}
          className="block transition-transform duration-150"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
          draggable={false}
        />
      </div>

      {/* Keyboard hint */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs text-gray-500">
        Esc to close · +/− to zoom · 0 to reset
      </div>
    </div>
  );
};
